import type { CSSProperties } from 'react';

export const BET_HISTORY_GRID_COLUMNS = 'repeat(4, minmax(68px, 1fr)) 36px';

export const BET_HISTORY_GRID_COLUMNS_WITH_BALANCES =
  'minmax(56px, 0.7fr) repeat(4, minmax(68px, 1fr)) minmax(84px, 1.3fr) 58px';

export const BET_HISTORY_SUMMARY_GRID_COLUMNS =
  '28px repeat(4, minmax(72px, 1fr)) 36px';

export const BET_HISTORY_DETAIL_GRID_COLUMNS =
  'minmax(72px, 0.9fr) minmax(56px, 0.7fr) repeat(3, minmax(64px, 1fr)) 36px';

export const BET_HISTORY_TABLE_MIN_WIDTH_PX = 570;

export const betHistoryModalStyles: Record<string, CSSProperties> = {
  overlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.72)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start',
    padding: '48px 16px 16px',
    zIndex: 10000,
    fontFamily: "'Inter', 'Roboto', system-ui, sans-serif",
  },
  /** SkyX handheld — centered horizontally, shifted upward vertically. */
  overlayMobileCentered: {
    alignItems: 'flex-start',
    justifyContent: 'center',
    paddingLeft: '16px',
    paddingRight: '16px',
    paddingTop: 'max(6vh, calc(env(safe-area-inset-top, 0px) + 8px))',
    paddingBottom: 'max(16px, env(safe-area-inset-bottom, 0px))',
  },
  panel: {
    background: '#1b1c1d',
    borderRadius: '12px',
    width: '100%',
    maxWidth: '560px',
    maxHeight: 'min(85vh, 640px)',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    boxShadow: '0 12px 40px rgba(0, 0, 0, 0.55)',
    border: '1px solid rgba(255, 255, 255, 0.08)',
  },
  panelBottomSheet: {
    maxWidth: '100%',
    borderRadius: '16px 16px 0 0',
    maxHeight: 'min(88vh, 720px)',
    border: 'none',
  },
  panelMobileCentered: {
    width: '100%',
    maxWidth: 'min(100%, 560px)',
    maxHeight: 'min(82vh, 620px)',
    borderRadius: '12px',
  },
  dragHandle: {
    width: '40px',
    height: '4px',
    borderRadius: '999px',
    background: 'rgba(255, 255, 255, 0.35)',
    margin: '10px auto 4px',
    flexShrink: 0,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '14px 18px',
    flexShrink: 0,
    background: '#232323',
    borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
  },
  headerMobile: {
    padding: '10px 16px',
  },
  horizontalScrollSliderWrap: {
    flexShrink: 0,
    padding: '6px 16px 2px',
  },
  horizontalScrollRange: {
    WebkitAppearance: 'none',
    appearance: 'none',
    width: '100%',
    height: '4px',
    borderRadius: '999px',
    background: 'rgba(255, 255, 255, 0.12)',
    outline: 'none',
    cursor: 'pointer',
    display: 'block',
    margin: 0,
  } as CSSProperties,
  headerTitleGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    minWidth: 0,
    flex: 1,
  },
  headerActions: {
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0,
    gap: '8px',
  },
  logsIconButton: {
    padding: 0,
    margin: 0,
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    lineHeight: 0,
    flexShrink: 0,
  },
  title: {
    margin: 0,
    fontSize: '14px',
    fontWeight: 700,
    color: '#ffffff',
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
  },
  logsLink: {
    color: '#28a909',
    fontSize: '12px',
    fontWeight: 600,
    textDecoration: 'underline',
    textUnderlineOffset: '2px',
    whiteSpace: 'nowrap',
    cursor: 'pointer',
  },
  closeButton: {
    background: 'none',
    border: 'none',
    color: '#8a8a8a',
    fontSize: '20px',
    lineHeight: 1,
    cursor: 'pointer',
    padding: '2px 6px',
  },
  tableScrollWrap: {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  tableHorizontalScroll: {
    flex: 1,
    minHeight: 0,
    overflowX: 'auto',
    overflowY: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    scrollBehavior: 'smooth',
    WebkitOverflowScrolling: 'touch',
  },
  tableContent: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100%',
    width: '100%',
    boxSizing: 'border-box',
    alignSelf: 'stretch',
  },
  tableHead: {
    display: 'grid',
    gridTemplateColumns: BET_HISTORY_GRID_COLUMNS,
    columnGap: '12px',
    rowGap: 0,
    padding: '6px 16px 4px',
    fontSize: '11px',
    fontWeight: 500,
    color: 'rgba(255, 255, 255, 0.45)',
    textTransform: 'none',
    flexShrink: 0,
    alignItems: 'end',
    width: '100%',
    boxSizing: 'border-box',
  },
  headCell: {
    minWidth: 0,
    lineHeight: 1.15,
  },
  headCellNumeric: {
    minWidth: 0,
    lineHeight: 1.15,
    textAlign: 'right',
  },
  body: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    overflowX: 'hidden',
    padding: '0 0 6px',
    WebkitOverflowScrolling: 'touch',
    scrollBehavior: 'smooth',
    scrollbarWidth: 'thin',
    scrollbarColor: 'rgba(255, 255, 255, 0.35) transparent',
  },
  statusMessage: {
    padding: '32px 20px',
    textAlign: 'center',
    color: 'rgba(255, 255, 255, 0.55)',
    fontSize: '14px',
  },
  balanceCell: {
    fontSize: '12px',
    lineHeight: 1.2,
    whiteSpace: 'nowrap',
    textAlign: 'right',
    minWidth: 0,
  },
  numericCell: {
    textAlign: 'right',
    minWidth: 0,
    whiteSpace: 'nowrap',
  },
  row: {
    display: 'grid',
    gridTemplateColumns: BET_HISTORY_GRID_COLUMNS,
    columnGap: '12px',
    rowGap: 0,
    alignItems: 'center',
    padding: '8px 16px',
    fontSize: '13px',
    color: 'rgba(255, 255, 255, 0.88)',
    borderBottom: '1px solid rgba(255, 255, 255, 0.04)',
    width: '100%',
    boxSizing: 'border-box',
  },
  rowWin: {
    background: 'rgba(40, 169, 9, 0.18)',
  },
  dateCell: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    lineHeight: 1.2,
  },
  dateTime: {
    fontSize: '13px',
    fontWeight: 500,
  },
  dateDay: {
    fontSize: '11px',
    opacity: 0.65,
  },
  multiplier: {
    fontWeight: 600,
    color: '#34b4ff',
  },
  multiplierWin: {
    fontWeight: 600,
    color: '#28a909',
  },
  cashOutWin: {
    fontWeight: 600,
    color: '#28a909',
  },
  winCheck: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '18px',
    height: '18px',
    borderRadius: '50%',
    background: '#28a909',
    flexShrink: 0,
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: '8px',
  },
  actionButton: {
    background: 'none',
    border: 'none',
    padding: 0,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '20px',
    height: '20px',
  },
  actionIcon: {
    width: '18px',
    height: '18px',
    objectFit: 'contain',
  },
  groupWrap: {
    display: 'flex',
    flexDirection: 'column',
    marginBottom: '8px',
    width: '100%',
  },
  expandButton: {
    background: 'none',
    border: '1px solid rgba(255, 255, 255, 0.28)',
    borderRadius: '4px',
    padding: 0,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '22px',
    height: '22px',
    color: 'rgba(255, 255, 255, 0.88)',
    flexShrink: 0,
  },
  expandIcon: {
    fontSize: '16px',
    lineHeight: 1,
    fontWeight: 600,
  },
  detailSection: {
    display: 'flex',
    flexDirection: 'column',
    padding: '0 8px 8px 28px',
  },
  detailHead: {
    display: 'grid',
    gridTemplateColumns: BET_HISTORY_DETAIL_GRID_COLUMNS,
    columnGap: '12px',
    padding: '8px 10px 4px',
    fontSize: '11px',
    fontWeight: 500,
    color: 'rgba(255, 255, 255, 0.45)',
    alignItems: 'end',
    width: '100%',
    boxSizing: 'border-box',
  },
  detailRow: {
    display: 'grid',
    gridTemplateColumns: BET_HISTORY_DETAIL_GRID_COLUMNS,
    columnGap: '12px',
    alignItems: 'center',
    padding: '8px 10px',
    fontSize: '13px',
    color: 'rgba(255, 255, 255, 0.88)',
    borderBottom: '1px solid rgba(255, 255, 255, 0.04)',
    width: '100%',
    boxSizing: 'border-box',
  },
};
